import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { returnOnlyNumber_toString } from '@utils/regex';
import { useQueryClient } from 'react-query';
import { Toast } from '@components/_atoms/Toast';
import { useTranslation } from 'next-i18next';

// components
import Button from '@components/_atoms/Button';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';
import PhoneInputBox from '@components/_templates/auth/SignUpContent/Step/PhoneInputBox';

// constants
import { ButtonColor, ButtonType } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';

// hooks
import { usePatchUserPhoneFetch } from '@hooks/fetch/useUserFetch';

// type
import { EditPhoneFormType } from '../types';
import ButtonCode from '@constants/log/buttonCode';

const Layout = styled.form``;

const ButtonBoxWrapper = styled.div`
  margin-top: 80px;

  @media ${MOBILE_MODE} {
    margin-top: 202px;
  }
`;

export default function PhoneEditForm() {
  const { t, i18n } = useTranslation('user');
  const ko = i18n.language === 'ko';
  const router = useRouter();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<EditPhoneFormType>({
    phone_code: ko ? '82' : '',
    phone: '',
  });
  const [errorMessage, setErrorMessage] = useState('');

  const disabled = !form.phone || !!errorMessage;

  const { patchUserPhoneFetchMutate } = usePatchUserPhoneFetch();

  // country code change
  const handleCountryCodeChange = (value: string) => {
    setForm((prev) => ({ ...prev, phone_code: value }));
  };

  // phone change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value === '') {
      setForm((prev) => ({ ...prev, phone: value }));
    } else {
      setForm((prev) => ({ ...prev, phone: returnOnlyNumber_toString(value) }));
    }

    checkValidation(value);
  };

  // check validation
  const checkValidation = (value: string) => {
    let message = '';

    if (!value) message = t('edit.require-error-message');
    setErrorMessage(message);
  };

  // phone 변경 submit
  const handleFormSubmit = () => {
    // +82 형식으로 변환
    const phone_code = '+' + form.phone_code;

    patchUserPhoneFetchMutate(
      { phone_code, phone: form.phone },
      {
        onSuccess: () => {
          router.back();
          Toast.success(t('edit.success-message'));
          queryClient.invalidateQueries(['user']);
        },
      },
    );
  };

  return (
    <Layout
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <PhoneInputBox
        form={form}
        errorMessage={errorMessage}
        handlePhoneChange={handlePhoneChange}
        handleCountryCodeChange={handleCountryCodeChange}
      />
      <ButtonBoxWrapper>
        <DefaultButtonBox
          button1={
            <Button
              text={t('edit.back-button')}
              type={ButtonType.Button}
              color={ButtonColor.OutlinedBlack}
              onClick={() => router.back()}
              steelbosoId={ButtonCode.BTN_BACK}
            />
          }
          button2={<Button disabled={disabled} text={t('edit.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} />}
        />
      </ButtonBoxWrapper>
    </Layout>
  );
}
